import { propertiesApi, type PropertyDto, type PropertyListItemDto } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import './rc-property-list-view';
import { RcPropertyListView } from './rc-property-list-view';
import { openPropertyForm, openPropertyImportForm } from './property-list-dialogs';
import { buildPropertyDetailsUrl } from './property-navigation';
import type { PropertyViewMode } from './property-shared';

type PropertyListEventDetail = {
  propertyId?: string;
  tab?: string;
  autoAction?: string;
};

const LIST_EVENTS = ['add', 'import', 'view-cards', 'view-list', 'edit', 'delete', 'open-details', 'open-tab', 'quick-add'] as const;

export class RcPropertyList extends HTMLElement {
  private properties: PropertyListItemDto[] = [];
  private viewMode: PropertyViewMode = 'cards';
  private disposeLocaleChange?: () => void;
  private initialRouteHandled = false;

  async connectedCallback(): Promise<void> {
    this.viewMode = (localStorage.getItem('property-view-mode') as PropertyViewMode) || 'cards';
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    LIST_EVENTS.forEach((eventName) => this.addEventListener(`property-list-${eventName}`, this.handleListEvent as EventListener));
    await this.loadProperties();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    LIST_EVENTS.forEach((eventName) => this.removeEventListener(`property-list-${eventName}`, this.handleListEvent as EventListener));
  }

  private async loadProperties(): Promise<void> {
    try {
      this.properties = await propertiesApi.getAll();
      this.render();
      this.handleInitialRouteTarget();
    } catch (error) {
      console.error('Failed to load properties:', error);
      this.innerHTML = `<div class="empty-state">${t('property.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-property-list-view></rc-property-list-view>';
    const view = this.querySelector('rc-property-list-view') as RcPropertyListView | null;
    if (!view) return;
    view.properties = this.properties.filter((property) => property.structureType !== 'Unit');
    view.viewMode = this.viewMode;
    view.render();
  }

  private handleListEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<PropertyListEventDetail>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'property-list-add':
        openPropertyForm({ onSaved: async () => this.loadProperties() });
        return;
      case 'property-list-import':
        openPropertyImportForm(async () => this.loadProperties());
        return;
      case 'property-list-view-cards':
        this.viewMode = 'cards';
        localStorage.setItem('property-view-mode', 'cards');
        this.render();
        return;
      case 'property-list-view-list':
        this.viewMode = 'list';
        localStorage.setItem('property-view-mode', 'list');
        this.render();
        return;
      case 'property-list-edit': {
        const property = this.findProperty(detail.propertyId);
        if (property) {
          openPropertyForm({ property, onSaved: async () => this.loadProperties() });
        }
        return;
      }
      case 'property-list-delete':
        if (detail.propertyId) await this.handleDelete(detail.propertyId);
        return;
      case 'property-list-open-details':
        this.openDetailsPage(detail.propertyId);
        return;
      case 'property-list-open-tab':
        this.openDetailsPage(detail.propertyId, detail.tab);
        return;
      case 'property-list-quick-add':
        this.openDetailsPage(detail.propertyId, detail.tab, detail.autoAction);
        return;
      default:
        return;
    }
  };

  private findProperty(propertyId?: string): PropertyDto | undefined {
    return this.properties.find((property) => property.id === propertyId);
  }

  private openDetailsPage(propertyId?: string, initialTab?: string, autoAction?: string, fromOverride?: string): void {
    const property = this.findProperty(propertyId);
    if (!property) return;
    const from = fromOverride ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(buildPropertyDetailsUrl(property.id, {
      tab: initialTab,
      autoAction,
      from,
    }));
  }

  private handleInitialRouteTarget(): void {
    if (this.initialRouteHandled) {
      return;
    }

    this.initialRouteHandled = true;
    const params = new URLSearchParams(window.location.search);
    const propertyId = params.get('property') ?? undefined;
    const property = this.findProperty(propertyId);
    if (!propertyId || !property) {
      return;
    }

    const tab = params.get('tab') ?? undefined;
    const autoAction = params.get('autoAction') ?? undefined;
    this.openDetailsPage(propertyId, tab ?? undefined, autoAction ?? undefined, '/properties/');
  }

  private async handleDelete(propertyId: string): Promise<void> {
    if (!await confirmDialog('Weet je zeker dat je dit pand wilt verwijderen? Alle gekoppelde huurders en documenten worden ook verwijderd.')) return;
    try {
      await propertiesApi.delete(propertyId);
      await this.loadProperties();
    } catch (error) {
      console.error('Failed to delete property:', error);
      toastService.error(error instanceof ApiError ? error.message : t('property.deleteError'));
    }
  }
}

if (!customElements.get('rc-property-list')) {
  customElements.define('rc-property-list', RcPropertyList);
}
